import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import PageHero from '../components/ui/PageHero';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { SectionHead } from '../components/ui/SectionHead';
import DlFacts from '../components/ui/DlFacts';
import FormField from '../components/ui/FormField';
import HCaptchaField from '../components/ui/HCaptchaField';
import Button from '../components/ui/Button';
import Notice from '../components/ui/Notice';
import Faq from '../components/ui/Faq';
import { entity } from '../content/en';
import { createContactFormMessage } from '../utils/contactFormMessageApi';
import { contactPageSchema, localBusinessSchema, breadcrumbSchema } from '../components/ui/schema';

const Contact = () => {
  const { t } = useTranslation('cy');
  const meta = t('contact.meta', { returnObjects: true });
  const fields = t('contact.form.fields', { returnObjects: true });
  const topics = t('contact.form.topics', { returnObjects: true });

  const [form, setForm] = useState({ name: '', email: '', topic: '', phone: '', message: '' });
  const [errors, setErrors] = useState({});
  const [consent, setConsent] = useState(false);
  const [token, setToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { status, message, reference }
  const captchaRef = useRef(null);

  const schema = [
    contactPageSchema(),
    localBusinessSchema(),
    breadcrumbSchema([{ label: 'Home', to: '/' }, { label: 'Contact', to: '/contact' }]),
  ];

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = fields.name.error;
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = fields.email.error;
    if (!form.topic) e.topic = fields.topic.error;
    if (!form.message.trim()) e.message = fields.message.error;
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;

    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    if (!token) {
      setResult({ status: 'error', message: t('contact.form.captchaExpired') });
      return;
    }
    if (!consent) {
      setErrors((er) => ({ ...er, consent: t('contact.form.errorSummary') }));
      return;
    }

    setSubmitting(true);
    setResult(null);
    let res;
    try {
      res = await createContactFormMessage({
        fullname: form.name,
        email: form.email,
        message_subject: form.topic,
        message: form.message,
        phone: form.phone,
        captcha_token: token,
        page_info: typeof window !== 'undefined' ? window.location.href : '',
        ext_info: { source_form: 'contact', topic: form.topic },
      });
    } finally {
      setSubmitting(false);
      captchaRef.current?.reset();
      setToken(null);
    }

    if (res.ok) {
      setResult({ status: 'success', reference: res.data && res.data.reference });
      setForm({ name: '', email: '', topic: '', phone: '', message: '' });
      setConsent(false);
    } else {
      setResult({ status: 'error', message: res.message || t('contact.form.serviceError') });
    }
  };

  const errorCount = Object.values(errors).filter(Boolean).length;

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/contact" schema={schema} />

      <PageHero
        id="contact-hero"
        eyebrow={t('contact.hero.eyebrow')}
        title={t('contact.hero.h1')}
        lead={t('contact.hero.lead')}
        breadcrumb={<Breadcrumbs items={[{ label: t('nav.contact') }]} />}
      >
        <div className="cy-grid cy-grid--2" style={{ marginTop: 32 }}>
          <a className="cy-card cy-card--link" href="#contact-form">
            <h2 className="cy-card-title">{t('contact.hero.general')}</h2>
            <span className="cy-card-arrow">{t('contact.hero.generalCta')}</span>
          </a>
          <Link className="cy-card cy-card--link" to="/submit-opportunity">
            <h2 className="cy-card-title">{t('contact.hero.opportunity')}</h2>
            <span className="cy-card-arrow">{t('contact.hero.opportunityCta')}</span>
          </Link>
        </div>
      </PageHero>

      {/* contact-channels */}
      <section className="cy-section cy-section--alt" id="contact-channels">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('contact.channels.h2')} lead={t('contact.channels.lead')} />
          <DlFacts
            rows={t('contact.channels.rows', { returnObjects: true }).map((r) => ({
              label: r.channel,
              value: r.value,
              href: r.href,
            }))}
          />
          <p className="cy-fineprint" style={{ marginTop: 24 }}>
            {t('contact.channels.hours')}{' '}
            <Link className="cy-link" to="/about#about-entity">{t('contact.channels.verify')}</Link>
          </p>
        </div>
      </section>

      {/* contact-location */}
      <section className="cy-section" id="contact-location">
        <div className="cy-container">
          <SectionHead title={t('contact.location.h2')} />
          <div className="cy-grid cy-grid--2" style={{ alignItems: 'center' }}>
            <div>
              <p className="cy-body" style={{ fontSize: 18, color: 'var(--cy-color-text-primary)' }}>{entity.address}</p>
              <p className="cy-body" style={{ marginTop: 12 }}>{t('contact.location.visit')}</p>
              <p className="cy-fineprint" style={{ marginTop: 12 }}>{t('contact.location.accessibility')}</p>
              <p style={{ marginTop: 24 }}>
                <a className="cy-btn cy-btn--secondary" href={t('contact.location.mapsUrl')} target="_blank" rel="noreferrer">
                  {t('contact.location.openMaps')} <span aria-hidden="true">↗</span>
                </a>
              </p>
            </div>
            <div className="cy-map" role="img" aria-label={t('contact.location.mapAlt')}>
              <p className="cy-fineprint" style={{ textAlign: 'center', margin: 0 }}>
                {entity.address}
                <br />
                <a className="cy-link" href={t('contact.location.mapsUrl')} target="_blank" rel="noreferrer">
                  {t('contact.location.openMaps')} <span aria-hidden="true">↗</span>
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* contact-expect */}
      <section className="cy-section cy-section--alt cy-zone--doc" id="contact-expect">
        <div className="cy-container">
          <SectionHead eyebrow={t('contact.expect.eyebrow')} title={t('contact.expect.h2')} lead={t('contact.expect.lead')} />
          <div className="cy-grid cy-grid--4">
            {t('contact.expect.steps', { returnObjects: true }).map((step, i) => (
              <div className="cy-card" key={step.title}>
                <span className="cy-stage__num">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="cy-card-title" style={{ marginTop: 12 }}>{step.title}</h3>
                <p className="cy-card-text">{step.body}</p>
              </div>
            ))}
          </div>
          <Notice variant="compliance" className="cy-notice--inline" style={{ marginTop: 32 }}>
            {t('contact.expect.boundary')}
          </Notice>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/approach#approach-gates">{t('contact.expect.cta')}</Link>
          </p>
        </div>
      </section>

      {/* contact-faq */}
      <section className="cy-section cy-zone--doc" id="contact-faq">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('contact.faq.h2')} lead={t('contact.faq.lead')} />
          <Faq items={t('contact.faq.items', { returnObjects: true })} idPrefix="contact-faq" />
          <p style={{ marginTop: 24 }}>
            <Link className="cy-link" to="/privacy">{t('contact.faq.privacyLink')}</Link>
          </p>
        </div>
      </section>

      {/* contact-form */}
      <section className="cy-section cy-section--alt cy-zone--doc" id="contact-form">
        <div className="cy-container cy-container--form">
          <SectionHead title={t('contact.form.h2')} lead={t('contact.form.lead')} />

          {result && result.status === 'success' ? (
            <div className="cy-panel cy-panel--success" tabIndex={-1} role="status">
              <div className="cy-panel__icon" style={{ color: 'var(--cy-color-success)' }}><FiCheckCircle /></div>
              <h2 className="cy-panel__title">{t('contact.after.successTitle')}</h2>
              <p className="cy-panel__body">{t('contact.after.successBody')}</p>
              {result.reference && (
                <p className="cy-panel__body">
                  {t('contact.after.reference')} <span className="cy-panel__ref">{result.reference}</span>
                </p>
              )}
              <div className="cy-panel__actions">
                <Link className="cy-link" to="/approach">{t('contact.after.next')}</Link>
                <a className="cy-link" href={entity.phoneHref}>{t('contact.after.call')} {entity.phone}</a>
                <Link className="cy-link" to="/submit-opportunity">{t('contact.after.submitLink')}</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate>
              {errorCount > 0 && (
                <div className="cy-error-summary" role="alert" tabIndex={-1}>
                  <p className="cy-error-summary__title">{t('contact.form.errorSummary')}</p>
                </div>
              )}

              <FormField id="c-name" label={fields.name.label} help={fields.name.help} required error={errors.name}>
                <input
                  id="c-name"
                  className="cy-input"
                  type="text"
                  name="name"
                  autoComplete="name"
                  value={form.name}
                  onChange={set('name')}
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'c-name-error' : 'c-name-help'}
                />
              </FormField>

              <FormField id="c-email" label={fields.email.label} help={fields.email.help} required error={errors.email}>
                <input
                  id="c-email"
                  className="cy-input"
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  value={form.email}
                  onChange={set('email')}
                  aria-invalid={!!errors.email}
                />
              </FormField>

              <FormField id="c-topic" label={fields.topic.label} help={fields.topic.help} required error={errors.topic}>
                <select id="c-topic" className="cy-select" name="topic" value={form.topic} onChange={set('topic')} aria-invalid={!!errors.topic}>
                  <option value="">—</option>
                  {topics.map((topic) => (
                    <option key={topic} value={topic}>{topic}</option>
                  ))}
                </select>
              </FormField>

              {(form.topic === 'Partnership' || form.topic === 'Other') && (
                <Notice variant="info" style={{ marginBottom: 24 }}>
                  <Link className="cy-link" to="/submit-opportunity">{t('contact.form.topicToOpportunity')}</Link>
                </Notice>
              )}

              <FormField id="c-phone" label={fields.phone.label} help={fields.phone.help} optional error={errors.phone}>
                <input
                  id="c-phone"
                  className="cy-input"
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={set('phone')}
                />
              </FormField>

              <FormField id="c-message" label={fields.message.label} help={fields.message.help} required error={errors.message}>
                <textarea
                  id="c-message"
                  className="cy-textarea"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={set('message')}
                  aria-invalid={!!errors.message}
                />
              </FormField>

              <label className="cy-checkbox" style={{ marginBottom: 24 }}>
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                <span>
                  {t('contact.form.consent')} <Link className="cy-link" to="/privacy">Privacy Policy</Link>
                </span>
              </label>
              {errors.consent && <p className="cy-error" role="alert">{errors.consent}</p>}

              <HCaptchaField ref={captchaRef} onToken={setToken} />

              <Notice variant="inline" style={{ margin: '16px 0 24px' }}>{t('contact.form.sensitive')}</Notice>

              <Button type="submit" ariaBusy={submitting} disabled={submitting}>
                {submitting ? 'Sending…' : t('contact.form.submit')}
              </Button>
            </form>
          )}

          {result && result.status === 'error' && (
            <div className="cy-panel cy-panel--error" role="alert" tabIndex={-1} style={{ marginTop: 32 }}>
              <div className="cy-panel__icon" style={{ color: 'var(--cy-color-error)' }}><FiAlertCircle /></div>
              <p className="cy-panel__body">{result.message}</p>
              <div className="cy-panel__actions">
                <a className="cy-link" href={entity.phoneHref}>{entity.phone}</a>
                <a className="cy-link" href={entity.emailHref}>{entity.email}</a>
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
